import DragHandleIcon from '@mui/icons-material/DragHandle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LiveHelpIcon from '@mui/icons-material/LiveHelp';
import SendIcon from '@mui/icons-material/Send';
import { Box, CircularProgress, debounce, Popper, TextField, Typography } from '@mui/material';
import { Actions, Guid, RagQueryPayload, Subjects } from 'ihub/common';
import { useCallback, useEffect, useRef, useState } from 'react';
import HttpServices from '../../../classes/http-services';
import { userHasPermissions } from '../../../functions/session-functions';

interface XYCoordinate {
	xPos: number;
	yPos: number;
}

interface BoxSize {
	width: number;
	height: number;
}

interface MsgHistoryItem {
	id: string;
	owner: string;
	message: string;
}

export const chatbotMsgHistoryKey = 'ChatBotHistory';

export default function RagChatWindow() {
	const minHeight = 600;
	const minWidth = 400;

	const botName = 'iHub Advisor';
	const boxRef = useRef<any>(null);
	const contentRef = useRef<HTMLDivElement>(null); // Ref for the last element within the Box
	const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
	const [ragQuery, setRagQuery] = useState<RagQueryPayload>({ queryString: '' });

	const init = (): MsgHistoryItem[] => {
		const temp = sessionStorage.getItem(chatbotMsgHistoryKey);
		if (temp) {
			return JSON.parse(temp) as MsgHistoryItem[];
		}
		return [
			{
				id: Guid(),
				owner: botName,
				message: `Hello!
I'd be happy to help you with iHub. Could you please let me know what specific aspect of iHub you'd like assistance with? For example, are you looking for information about:
- Getting started with iHub
- Specific features or functionality
- Account management
- Or something else?`,
			},
		];
	};

	const [messageHistory, setMessageHistory] = useState<MsgHistoryItem[]>(init());
	const [isLoading, setIsLoading] = useState(false);
	const [isEnabled, setIsEnabled] = useState(false);
	const isResizing = useRef(false); // Mutable ref to track whether the user is currently resizing the box
	const [size, setSize] = useState<BoxSize>({ height: minHeight, width: minWidth });
	const initialPosition = useRef<XYCoordinate>({ xPos: 0, yPos: 0 });
	const initialSize = useRef<BoxSize>(null);

	useEffect(() => {
		HttpServices.ragAi.isEnabled({
			success: value => {
				if (value === 'true') setIsEnabled(true);
				else setIsEnabled(false);
			},
		});
	}, []);

	const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (anchorEl) {
			handleClose();
			return;
		}
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const messageHistoryToKeep = 20;

	const handleMessageUpdate = (prevState: MsgHistoryItem[], owner: string, message: string) => {
		let temp = [...prevState];
		temp.push({ id: Guid(), owner: owner, message: message });
		if (temp.length > messageHistoryToKeep) {
			temp = temp.slice(temp.length - messageHistoryToKeep);
		}
		sessionStorage.setItem(chatbotMsgHistoryKey, JSON.stringify(temp));
		return temp;
	};

	const sendQuestion = () => {
		const tmpMsg = ragQuery.queryString.trim();
		if (!tmpMsg) return;
		setIsLoading(true);
		setMessageHistory(prevState => {
			return handleMessageUpdate(prevState, 'You', tmpMsg);
		});
		scrollIntoView();
		const payload = { ...ragQuery };
		setRagQuery(prevState => {
			const temp = { ...prevState };
			temp.queryString = '';
			return temp;
		});
		HttpServices.ragAi.sendQuestion(payload, {
			success: value => {
				setMessageHistory(prevState => {
					return handleMessageUpdate(prevState, botName, value);
				});
				scrollIntoView();
				setIsLoading(false);
			},
			error: message => {
				setMessageHistory(prevState => {
					return handleMessageUpdate(prevState, botName, message);
				});
				setIsLoading(false);
			},
		});
	};

	const scrollIntoView = () => {
		setTimeout(() => {
			if (contentRef.current) {
				contentRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
			}
		}, 500);
	};

	const renderMessages = () => {
		return messageHistory.map((messageItem, index) => {
			return (
				<Box
					key={messageItem.id}
					sx={{ py: 1 }}
					ref={messageHistory.length === index + 1 && !isLoading ? contentRef : undefined}
				>
					<TextField
						label={messageItem.owner}
						value={messageItem.message}
						slotProps={{
							input: { readOnly: true },
						}}
						multiline
					/>
				</Box>
			);
		});
	};

	const open = Boolean(anchorEl);
	const id = open ? 'simple-popover' : undefined;

	const handleMouseDown = (clientX: number, clientY: number) => {
		isResizing.current = true; // Set resizing state to true
		initialPosition.current.xPos = clientX;
		initialPosition.current.yPos = clientY;
		initialSize.current = size;
		document.body.style.cursor = 'grabbing';
		document.addEventListener('mousemove', handleMouseMove); // Attach mousemove event listener to track mouse movement during resize
		document.addEventListener('mouseup', handleMouseUp); // Attach mouseup event listener to end resizing when mouse button is released
	};

	// `useCallback` to memoize the `handleMouseMove` function to prevent it from being recreated on every render
	const handleMouseMove = useCallback(
		debounce(e => {
			if (isResizing.current && initialSize.current) {
				// Check if resizing is active
				const newWidth = initialSize.current.width + (initialPosition.current.xPos - e.clientX);
				const newHeight = initialSize.current.height + (e.clientY - initialPosition.current.yPos);
				if (newHeight >= minHeight && newWidth >= minWidth) {
					setSize({ height: newHeight, width: newWidth });
				} else if (newHeight >= minHeight) {
					setSize(prevState => {
						const temp = { ...prevState };
						temp.height = newHeight;
						return temp;
					});
				} else if (newWidth >= minWidth) {
					setSize(prevState => {
						const temp = { ...prevState };
						temp.width = newWidth;
						return temp;
					});
				}
			}
		}, 1), // Debounce delay to limit the rate at which `handleMouseMove` is called
		[]
	);

	const handleMouseUp = () => {
		isResizing.current = false; // Set resizing state to false

		// Remove the event listeners when done to prevent memory leaks and unnecessary event handling
		document.body.style.cursor = 'default';
		document.removeEventListener('mousemove', handleMouseMove);
		document.removeEventListener('mouseup', handleMouseUp);
	};

	// Cleanup effect to ensure that event listeners are removed if the component unmounts during a resize operation
	useEffect(() => {
		return () => {
			// Remove event listeners to prevent memory leaks
			document.removeEventListener('mousemove', handleMouseMove);
			document.removeEventListener('mouseup', handleMouseUp);
		};
	}, [handleMouseMove]); // `handleMouseMove` is a dependency to ensure cleanup occurs properly if the callback changes

	const getInputHeight = () => {
		const element = document.getElementById('rag-query-input');
		if (element?.style.height) {
			return 80 + Number.parseInt(element.style.height);
		}
		return 100;
	};

	const render = () => {
		if (
			isEnabled &&
			userHasPermissions(Subjects.RagAi, Actions.create) &&
			userHasPermissions(Subjects.RagAi, Actions.read)
		) {
			return (
				<>
					<Box
						id={'RAG-Chat-Box-btn'}
						key={'RAG-Chat-Box-btn'}
						onClick={handleClick}
						sx={{
							display: 'flex',
							alignContent: 'center',
							justifyContent: 'center',
							alignItems: 'center',
							borderRadius: 1,
							p: 1,
							mx: 1,
							':hover': {
								backgroundColor: theme => theme.palette.primary.dark,
								cursor: 'pointer',
							},
						}}
					>
						<LiveHelpIcon />
						{!open && <ExpandMoreIcon />}
						{open && <ExpandLessIcon />}
					</Box>
					<Popper
						id={id}
						open={open}
						anchorEl={anchorEl}
						placement="bottom-end"
						sx={{
							zIndex: 99999,
							width: size.width,
							height: size.height,
							backgroundColor: theme => theme.palette.background.paper,
							p: 2,
						}}
					>
						<Box
							ref={boxRef}
							sx={{
								width: '100%',
								m: 0,
								p: 0,
								pr: 1,
								height: size.height - getInputHeight(),
								overflowY: 'scroll',
							}}
						>
							{renderMessages()}
							{isLoading && (
								<Box
									ref={contentRef}
									style={{
										width: '100%',
										height: 100,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<Box
										style={{
											display: 'flex',
											flexDirection: 'column',
											alignItems: 'center',
										}}
									>
										<CircularProgress />
										<Typography>{'Thinking...'}</Typography>
									</Box>
								</Box>
							)}
						</Box>
						<Box sx={{ width: '100%', m: 0, p: 0, display: 'flex', flexDirection: 'row' }}>
							<TextField
								id={'rag-query-input'}
								key={'rag-query-input'}
								label={'Question'}
								value={ragQuery.queryString}
								onChange={value => {
									setRagQuery(prevState => {
										const temp: RagQueryPayload = { ...prevState };
										temp.queryString = value.target.value;
										return temp;
									});
								}}
								sx={{ pb: 1 }}
								onKeyUp={e => {
									e.preventDefault();
									if (e.key === 'Enter' && !e.shiftKey) {
										sendQuestion();
									}
								}}
								multiline
							/>
							<SendIcon
								id={'RAG-send-question'}
								key={'RAG-send-question'}
								sx={{
									height: 55,
									width: 40,
									borderRadius: 1,
									px: 1,
									mx: 1,
									color: theme =>
										!ragQuery.queryString ? theme.palette.action.disabled : undefined,
									':hover': {
										backgroundColor: theme => theme.palette.primary.dark,
										cursor: 'pointer',
									},
								}}
								onClick={sendQuestion}
							/>
						</Box>
						<DragHandleIcon
							id={`RAG-drag-handle`}
							key={`RAG-drag-handle`}
							onMouseDown={e => {
								handleMouseDown(e.clientX, e.clientY);
							}}
							sx={{
								position: 'relative',
								top: -10,
								left: -14,
								borderRadius: 1,
								':hover': {
									backgroundColor: theme => theme.palette.primary.dark,
									cursor: 'grab',
								},
							}}
						/>
					</Popper>
				</>
			);
		} else {
			return <></>;
		}
	};

	return render();
}
